/**
 * The provisioning rules, client side.
 *
 * A deliberate mirror of `api/routes/chargePointProvisioning.ts`. The server is
 * the gate — nothing here is trusted — but a rejected id that costs a round trip
 * also costs an audited denial, and an operator typing an id deserves to be told
 * about a stray slash before they submit. Both halves cite the same reasons so a
 * drift is visible rather than silent.
 */

import type { Permission } from "./permissions";

/**
 * Letters, digits, dot, underscore, hyphen. The station's id is the last path
 * segment of its own WebSocket URL (spec 3.1), so a slash or a space is not a
 * style violation — it is a station that can never authenticate.
 *
 * Mirrors `domain/chargePoint/identifier.ts`, whose header carries the full
 * argument for an allowlist over a denylist.
 */
const STATION_ID_PATTERN = /^[A-Za-z0-9._-]+$/;

/** `parseChargePointId`'s own ceiling. */
const STATION_ID_MAX = 255;

/** The schema's CHECK, and a day, past which a heartbeat is not a liveness signal. */
export const HEARTBEAT_MIN_S = 30;
export const HEARTBEAT_MAX_S = 86_400;

/**
 * A message for a field the operator is still filling in, or `undefined` when
 * there is nothing to say. An empty field is never an error here — the form's
 * own required/optional handling decides that, and shouting at someone for not
 * having typed yet is noise.
 */
export function stationIdError(raw: string): string | undefined {
  if (raw === "") return undefined;

  if (raw.length > STATION_ID_MAX) {
    return `At most ${STATION_ID_MAX} characters.`;
  }

  /**
   * The colon before the charset, and with its own sentence.
   *
   * It is the character an operator is most likely to try — vendor serials are
   * written `SIM:0001` — and the only one whose consequence is invisible at the
   * point of the mistake. The id is the username in the station's Basic auth,
   * where a colon is what separates username from password (RFC 7617), so a
   * station registered with one is accepted here and then fails authentication
   * forever, with a 401 that names a credential problem. Told only "invalid
   * character", the operator's next move is to re-issue the password.
   */
  if (raw.includes(":")) {
    return "A colon cannot be used. The id is the username in the station's Basic auth credential, and a colon is what separates username from password there — a station whose id has one can never connect.";
  }

  if (!STATION_ID_PATTERN.test(raw)) {
    return "Letters, digits, dot, underscore and hyphen only — no spaces or slashes. The id is the last part of the station's WebSocket URL.";
  }

  return undefined;
}

/** The server's own bound on `depot.name`. */
export const DEPOT_NAME_MAX = 120;

export function depotNameError(raw: string): string | undefined {
  if (raw === "") return undefined;

  if (raw.trim() === "") return "A site needs a name.";
  if (raw.length > DEPOT_NAME_MAX) return `At most ${DEPOT_NAME_MAX} characters.`;

  return undefined;
}

export interface DepotDraft {
  readonly name: string;
}

/**
 * The `PATCH /depots/:id` body for an edit, or `undefined` when there is nothing
 * valid to send — which is what keeps the save button disabled.
 *
 * The name is the only thing a depot edit can change.
 */
export function depotChanges(
  depot: { readonly name: string },
  draft: DepotDraft,
): { name: string } | undefined {
  const name = draft.name.trim();
  if (name === "" || depotNameError(draft.name) !== undefined) return undefined;
  return name === depot.name ? undefined : { name };
}

/**
 * The nameplate's ceiling at 320 px, mirroring the server's `DISPLAY_LABEL_MAX`
 * and the column's CHECK.
 */
export const DISPLAY_LABEL_MAX = 60;

/**
 * The driver-facing name, which is optional everywhere it appears — a station
 * nobody has named shows its id, which is what the sticker says.
 *
 * Whitespace-only is an error rather than a silent no-op: the server refuses it,
 * and a form that trimmed it to nothing and submitted anyway would look like it
 * had saved a blank name.
 */
export function displayLabelError(raw: string): string | undefined {
  if (raw === "") return undefined;

  if (raw.trim() === "") return "A name needs more than spaces. Leave it empty instead.";
  if (raw.trim().length > DISPLAY_LABEL_MAX) {
    return `At most ${DISPLAY_LABEL_MAX} characters — it has to fit a phone screen.`;
  }

  return undefined;
}

/**
 * A depot's zone decides where its allowance months and weeks start, so a zone the
 * server cannot resolve is refused there — and refused here first, against the
 * same authority. `Intl` is that authority in both places: the zone database
 * changes, and a hardcoded list would be wrong the first time it did.
 */
export function timezoneError(raw: string): string | undefined {
  if (raw === "") return undefined;

  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: raw });
    return undefined;
  } catch {
    return "Not a recognised IANA timezone. Use a name like Asia/Phnom_Penh, not an abbreviation like ICT.";
  }
}

/** The deployment's default, and the zone every seeded depot uses. */
const DEFAULT_TIMEZONE = "Asia/Phnom_Penh";

/**
 * The zones to offer in a picker.
 *
 * `Intl.supportedValuesOf` is the browser's own list where it exists. Where it
 * does not, this falls back to a short set rather than to nothing — a depot form
 * with an empty zone list is a form that cannot be submitted, and the field is
 * validated on both sides anyway.
 */
export function timezoneOptions(): readonly string[] {
  const supported = Intl.supportedValuesOf?.("timeZone") ?? [];

  const zones = supported.length > 0 ? supported : ["UTC", "Asia/Bangkok", "Asia/Singapore"];

  // Default first, so the common case is the one already selected.
  return [DEFAULT_TIMEZONE, ...zones.filter((zone) => zone !== DEFAULT_TIMEZONE)];
}

/** Empty means "use the deployment's default", which is not an error. */
export function heartbeatError(raw: string): string | undefined {
  if (raw === "") return undefined;

  if (!/^\d+$/.test(raw)) return "Whole seconds only.";

  const value = Number(raw);
  if (value < HEARTBEAT_MIN_S || value > HEARTBEAT_MAX_S) {
    return `Between ${HEARTBEAT_MIN_S} and ${HEARTBEAT_MAX_S} seconds.`;
  }

  return undefined;
}

export interface HeartbeatPushOutcome {
  readonly tone: "ok" | "warn";
  readonly text: string;
}

/**
 * What the station did with an edited heartbeat, as a sentence.
 *
 * The value is saved whatever the answer (`routes/chargePointHeartbeat.ts`), so
 * every sentence starts there. What varies is whether the station is using it
 * yet, and a refusal must not read like a success: green for "applied, or will be
 * at the next boot as designed", amber for "the station has not taken it".
 */
export function heartbeatPushOutcome(status: string, intervalS: number): HeartbeatPushOutcome {
  switch (status) {
    case "Accepted":
      return { tone: "ok", text: `Saved. The station accepted ${intervalS}s and is using it now.` };
    case "NotConnected":
      return {
        tone: "ok",
        text: `Saved. The station is offline; it is told ${intervalS}s when it next boots.`,
      };
    case "RebootRequired":
      return {
        tone: "warn",
        text: `Saved. The station accepted ${intervalS}s but only uses it after it restarts.`,
      };
    case "Failed":
      return {
        tone: "warn",
        text: `Saved, but the station did not answer. It is told ${intervalS}s at its next boot.`,
      };
    default:
      return {
        tone: "warn",
        text: `Saved, but the station answered ${status} and is still on its old interval. It is told ${intervalS}s again at its next boot.`,
      };
  }
}

export type Retirement = "hide" | "unhide" | "delete";

/**
 * Which retirement a station's page offers, or none.
 *
 * Hide what has a history, delete what has none — the server's rule
 * (`api/routes/chargePointLifecycle.ts`), mirrored so the page shows the one
 * button that will work rather than both with one refused. A hidden station only
 * ever offers its way back.
 */
export function retirement(
  station: { readonly hiddenAt: string | null; readonly hasHistory: boolean },
  can: (permission: Permission) => boolean,
): Retirement | undefined {
  const action: Retirement =
    station.hiddenAt !== null ? "unhide" : station.hasHistory ? "hide" : "delete";
  const permission = action === "delete" ? "charge_points.delete" : "charge_points.hide";
  return can(permission) ? action : undefined;
}
